import { type ReactElement, useState } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import ResponsiveImage from "@gatsby-plugin-generic-page/components/ResponsiveImage"
import type {
  StepSectionFlatData,
  StepSectionItem,
} from "@gatsby-plugin-generic-page/fragments/stepSection"
import { loadIcon as loadSystemIcon } from "@ionos-web-design-system/icon/system/loader"
import { Stepper, Text, useDynamicIcon } from "@ionos-web-design-system/react"
import isEmpty from "~/lib/isEmpty"

const DEFAULT_INACTIVE_ICON_NAME = "plus"
const DEFAULT_ACTIVE_ICON_NAME = "minus"

type StepItemProps = {
  step: StepSectionItem
  isActive: boolean
}

function StepItem({
  step,
  isActive,
}: StepItemProps): ReactElement<StepItemProps> {
  const inactiveIconId = step.udsIcon?.iconId ?? DEFAULT_INACTIVE_ICON_NAME
  const activeIconId = step.activeUdsIcon?.iconId ?? DEFAULT_ACTIVE_ICON_NAME

  const { icon: inactiveIcon } = useDynamicIcon(loadSystemIcon, inactiveIconId)
  const { icon: activeIcon } = useDynamicIcon(loadSystemIcon, activeIconId)

  return (
    <Stepper.Step
      icon={inactiveIcon ?? undefined}
      activeIcon={activeIcon ?? undefined}
    >
      <div className="bg-surface-subtle p-4 rounded-md">
        {!isActive ? (
          <div className="flex flex-col gap-5 sm:flex-row sm:justify-between">
            <Stepper.Step.Label className="text-3xl">
              {step.title}
            </Stepper.Step.Label>

            {!isEmpty(step.thumbnail) ? (
              <Stepper.Step.Description>
                <ResponsiveImage
                  images={step.thumbnail}
                  className="max-w-[7.5rem]"
                />
              </Stepper.Step.Description>
            ) : null}
          </div>
        ) : null}

        <Stepper.Step.Panel>
          <div className="flex flex-wrap gap-5 flex-col md:flex-row">
            <div className="flex flex-col flex-1 md:min-w-[17.125rem]">
              <Text variant="heading3xl" className="mb-3">
                {step.title}
              </Text>

              {!isEmpty(step.description) ? (
                <MarkdownText>{step.description}</MarkdownText>
              ) : null}

              {!isEmpty(step.ctaElement) ? (
                <div className="mt-4">
                  <CtaElement ctaElement={step.ctaElement} />
                </div>
              ) : null}
            </div>

            {!isEmpty(step.image) ? (
              <div className="flex-1 md:min-w-[17.125rem]">
                <ResponsiveImage images={step.image} />
              </div>
            ) : null}
          </div>
        </Stepper.Step.Panel>
      </div>
    </Stepper.Step>
  )
}

export default function StepSection({
  header,
  steps,
  sectionSettings,
}: StepSectionFlatData): ReactElement<StepSectionFlatData> {
  const [activeStep, setActiveStep] = useState(0)

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isEmpty(header) ? (
          <div className="mb-12">
            <Header {...header} />
          </div>
        ) : null}

        <Stepper
          orientation="vertical"
          activeStep={activeStep}
          onChange={setActiveStep}
          className="max-w-[55.875rem] mx-auto"
        >
          {steps.map((step, index) => (
            <StepItem
              key={step.title}
              step={step}
              isActive={index === activeStep}
            />
          ))}
        </Stepper>
      </BaseContainer>
    </BaseSection>
  )
}
